import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import TalentIgConnect, { type TalentIgEntry } from "../../../components/TalentIgConnect";
import { igConnectStage } from "../../../components/IgConnectSteps";
import type { OnboardingData } from "../../../hooks/useOnboardingState";
import type { IgDmRail } from "../../../hooks/useInstagramDmVerify";
import { instagramApi } from "../../../services/instagram";
import { startInstagramSignup } from "../../../lib/instagramLogin";
import type { SignupIdentity } from "../../../types/auth";
import { t } from "../../../i18n";

// Talent sign-up, step 2 of 3: Connect Instagram (build 54, the founder's
// option B; client/.claude/1.5.3-connect-socials-v3.html). Shown only while
// the sign-in check said both Instagram switches are on (data.igStep).
//
// The account doesn't exist yet, so every call carries the Google / Apple
// sign-up token, and what Instagram proves waits on a claim (the server's
// lib/identity/instagramSignup.js) that register attaches at the bank step.
//
//   Connect → Meta's Instagram sign-in (Business / Creator)
//       web: the page leaves (the sign-up is saved first) and comes back
//            through InstagramCallbackPage as /register?igSignup=<status>
//       app: the in-app sheet; the same return by deep link, and a quiet
//            re-check of the claim when the sheet just closes
//     done     → read the claim → "Instagram connected!" → Continue → the bank
//     personal → "Say hi on Instagram" → connected → the bank
//     taken / didn't go through → Connect again, with one line
//   Skip for now → the bank. Back → the profile.
//
// Nothing here can stop a sign-up: Skip is always there, and register never
// waits on Instagram.

type Props = {
  data: OnboardingData;
  updateData: (updates: Partial<OnboardingData>) => void;
  /** On to the bank (Skip, or Continue once connected). */
  onNext: () => void;
  /** Back to the profile. */
  onBack: () => void;
  /** Leaving for Instagram's sign-in: save the sign-up so far first. */
  saveBeforeLeaving: (over?: Partial<OnboardingData>) => Promise<void>;
  /** "Step 2 of 3". */
  strip: React.ReactNode;
};

type Screen = { entry: TalentIgEntry; username?: string; note?: string; key: number };

const identityOf = (d: OnboardingData): SignupIdentity | null => {
  if (d.authMethod === "google" && d.googleCredential) return { provider: "google", credential: d.googleCredential };
  if (d.authMethod === "apple" && d.appleCredential?.idToken) return { provider: "apple", idToken: d.appleCredential.idToken };
  return null;
};

const TAKEN = () => t("editProfileSheet.thisInstagramIsAlready");
const DIDNT_GO = () => t("editProfileSheet.thatDidnTGo");

// The first screen, from what the round trip said on the way back.
function firstScreen(returned: string | null, d: OnboardingData): Screen | null {
  if (d.igUsername) return { entry: "done", username: d.igUsername, key: 0 };
  if (returned === "done") return null; // read the claim first
  if (returned === "personal") return { entry: "personal", key: 0 };
  if (returned === "taken") return { entry: "connect", note: TAKEN(), key: 0 };
  if (returned === "failed" || returned === "expired") return { entry: "connect", note: DIDNT_GO(), key: 0 };
  return { entry: "connect", key: 0 };
}

export default function StepConnectInstagram({ data, updateData, onNext, onBack, saveBeforeLeaving, strip }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  // Read once: how Instagram's sign-in ended, if this is the way back.
  const [returned] = useState(() => new URLSearchParams(location.search).get("igSignup"));
  const [screen, setScreen] = useState<Screen | null>(() => firstScreen(returned, data));
  const claimRef = useRef(data.igClaim);
  const dataRef = useRef(data);
  dataRef.current = data;

  const show = (next: Omit<Screen, "key">) => setScreen((s) => ({ ...next, key: (s?.key ?? 0) + 1 }));
  const connected = (username: string) => updateData({ igUsername: username, igClaim: claimRef.current });

  // What Instagram's sign-in proved, read from the claim. `quiet`: the phone's
  // sheet merely closed — nothing back yet means stay put, no line.
  const recheck = async (quiet: boolean) => {
    const claim = claimRef.current;
    let r;
    try {
      r = claim ? await instagramApi.signupClaim(claim) : null;
    } catch {
      r = null;
    }
    if (r?.status === "verified" && r.username) {
      connected(r.username);
      show({ entry: "done", username: r.username });
    } else if (r?.status === "personal") show({ entry: "personal" });
    else if (r?.status === "taken") show({ entry: "connect", note: TAKEN() });
    else if (!quiet) show({ entry: "connect", note: DIDNT_GO() });
  };

  // The way back: the result leaves the URL (a refresh mustn't replay it).
  useEffect(() => {
    if (!returned) return;
    const rest = new URLSearchParams(location.search);
    rest.delete("igSignup");
    const search = rest.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : "", hash: location.hash }, { replace: true });
    if (returned === "done" && !dataRef.current.igUsername) void recheck(false);
    // Once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The message, for a sign-up: the same screens on the sign-up's endpoints.
  const rail: IgDmRail = useMemo(
    () => ({
      start: async () => {
        const who = identityOf(dataRef.current);
        if (!who) throw new Error("no sign-in");
        const r = await instagramApi.signupDmStart({ ...who, ...(claimRef.current ? { claim: claimRef.current } : {}) });
        claimRef.current = r.claim;
        updateData({ igClaim: r.claim });
        return r;
      },
      status: (sessionId) => instagramApi.signupDmStatus(claimRef.current, sessionId),
      afterVerified: async () => null,
    }),
    [updateData]
  );

  const startLogin = () => {
    const who = identityOf(dataRef.current);
    if (!who) return Promise.resolve(false);
    return startInstagramSignup({
      identity: who,
      claim: claimRef.current || undefined,
      onClaim: (claim) => {
        claimRef.current = claim;
        updateData({ igClaim: claim, igUsername: "" });
      },
      beforeLeaving: (claim) => saveBeforeLeaving({ igClaim: claim, igUsername: "" }),
    });
  };

  if (!screen) {
    // Reading what came back: the stage, a quiet spinner, no buttons yet.
    return igConnectStage("checking", {
      back: null,
      content: (
        <div className="fcf-wait" role="status" aria-label={t("instagramCallbackPage.oneMoment")}>
          <span className="fcf-spinner" aria-hidden="true" />
        </div>
      ),
      footer: null,
    });
  }

  return (
    <TalentIgConnect
      key={screen.key}
      name={data.name}
      avatarUrl={data.avatarPreview}
      entry={screen.entry}
      username={screen.username}
      note={screen.note}
      startLogin={startLogin}
      rail={rail}
      strip={strip}
      over={false}
      onVerified={connected}
      onTaken={() => show({ entry: "connect", note: TAKEN() })}
      onSheetClosed={() => void recheck(true)}
      onBack={onBack}
      onClose={onNext}
    />
  );
}
